import { useSearchParams } from "react-router-dom";
import { useAccess } from "../../auth/useAccess";
import { AdminScreen, type AdminTab } from "./AdminScreen";
import { BrandingTab } from "./components/BrandingTab";
import { UsersRolesTab } from "./components/UsersRolesTab";
import { AuditLogTab } from "./components/AuditLogTab";

type TabId = "branding" | "users" | "audit";

/**
 * Settings — the second of the canvas's two administration screens: Branding &
 * colors, Users & roles and the Audit log.
 *
 * Each tab is gated on the module that owns it rather than on one blanket
 * "settings" check, because they are three different authorities: deciding
 * what the product looks like, deciding who administers it, and reading what
 * everyone did. A principal sees the tabs they hold and no others, and the
 * screen says so plainly when that is none of them.
 *
 * Two of the three have no endpoint behind them in this application and the
 * third can only preview. Each tab says which endpoints it would need, in
 * place, rather than pretending — see `BackendUnavailable`.
 */
export function DeploymentSettingsPage(): JSX.Element {
  const { can, atLeast } = useAccess();
  const [params, setParams] = useSearchParams();

  const canReadBranding = atLeast("settings", "view");
  const canManageBranding = atLeast("settings", "edit");
  const canReadUsers = can("authorization.read") || can("users.read");
  // Three different authorities, because the API checks three different
  // actions: granting a role, creating an account, and ending one. A role that
  // holds only one of them gets only that control rather than a screen of
  // buttons that 403.
  const canAssignRoles = can("authorization.assign_roles");
  const canInvite = can("users.create");
  const canSuspend = can("users.disable");
  const canRemove = can("users.delete");
  const canReadAudit = can("audit.read");
  const canExportAudit = can("audit.export");

  const tabs: AdminTab<TabId>[] = [
    {
      id: "branding",
      label: "Branding & colors",
      lead: "Colors, logo and wordmark for this deployment.",
      visible: canReadBranding,
    },
    {
      id: "users",
      label: "Users & roles",
      lead: "Who administers this deployment, and at what level.",
      visible: canReadUsers,
    },
    {
      id: "audit",
      label: "Audit log",
      lead: "Everything that happened in this deployment, newest first.",
      visible: canReadAudit,
    },
  ];

  const visible = tabs.filter((tab) => tab.visible);
  const requested = params.get("tab") as TabId | null;
  const active = visible.find((tab) => tab.id === requested)?.id ?? visible[0]?.id ?? "branding";

  if (visible.length === 0) {
    return (
      <AdminScreen<TabId> title="Settings" tabs={tabs} active={active} onSelect={() => undefined}>
        <div className="set-list set-list-empty">
          <p className="field-error" role="alert">
            You do not have permission to view any settings.
          </p>
        </div>
      </AdminScreen>
    );
  }

  return (
    <AdminScreen<TabId>
      title="Settings"
      tabs={tabs}
      active={active}
      onSelect={(id) => setParams({ tab: id }, { replace: true })}
    >
      {active === "branding" && <BrandingTab canManage={canManageBranding} />}
      {active === "users" && (
        <UsersRolesTab
          canManage={canAssignRoles}
          canInvite={canInvite}
          canSuspend={canSuspend}
          canRemove={canRemove}
        />
      )}
      {active === "audit" && <AuditLogTab canExport={canExportAudit} />}
    </AdminScreen>
  );
}
